import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, Box,  Typography, } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { useSnackbar } from 'contexts/SnackbarContextProvider';
import { useTranslation } from "react-i18next";
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';

import DeleteLabelModal from './DeleteLabel'
import "../../css/main.css";
import "../../css/components/Modals/CreateNewTask.css";
import LabelsComponent from './LabelsComponent';

/* eslint-disable react-hooks/exhaustive-deps */
const getRandomHex = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};

export default function NewTaskModal({
    openNewTaskModal,
    handleTaskModalClose,
    handleSaveTask,
    avoidMisfire,
    DeleteLabel,
    AddLabelToBoard,
    UpdateLabel,
    myBoard,
    setMyBoard,
    workspace_id,
}) {
    const { t } = useTranslation();
    const workspaceContext = useWorkspaces();
    const myWorkspaces = workspaceContext.workspaces;
    const [localName, setLocalName] = useState('');
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [, setOpenLabelDialog] = useState(false);
    const [, setLabelTitle] = useState('');
    const [labelAdding, setLabelAdding] = useState(false);
    const [, setLabelColor] = useState(getRandomHex());
    const [labelEditing, setLabelEditing] = useState(false);
    const [, setLabelEditingTitle] = useState('');
    const [, setLabelEditingColor] = useState('');
    const [labelEditingID, setLabelEditingID] = useState(null);
    const { showSnackbar } = useSnackbar();
    const [pastedCoverImage, setPastedCoverImage] = useState(null);
    const [pastedCoverImageFile, setPastedCoverImageFile] = useState(null);

    const [openDeleteLabelModal, setOpenDeleteLabelModal] = useState(false);
    const [labelIdToDelete, setLabelIdToDelete] = useState(null);
    const [openLabelsModal, setOpenLabelsModal] = useState(false);
    const [labelToEdit, setLabelToEdit] = useState(false);

    const DeleteLabelModalOpen = (label_id) => {
        setLabelIdToDelete(label_id)
        setOpenDeleteLabelModal(true);
    };

    const DeleteLabelModalClose = () => {
        setLabelIdToDelete(null)
        setOpenDeleteLabelModal(false);
    };

    const handleLocalNameChange = (event) => {
        setLocalName(event.target.value);
    };

    const handleLabelChange = (label) => {
        setSelectedLabels((prevSelectedLabels) =>
            prevSelectedLabels.some((selectedLabel) => selectedLabel.id === label.id)
                ? prevSelectedLabels.filter((selectedLabel) => selectedLabel.id !== label.id)
                : [...prevSelectedLabels, label]
        );
    };

    const handleSaveClick = async () => {
        if (localName.trim() === '') {
            showSnackbar(t("modal.task_empty"), 'error');
            return;
        }
        const selectedLabelIds = selectedLabels.map((label) => label.id);

        await handleSaveTask(localName, selectedLabelIds, pastedCoverImageFile);

        setLocalName("");
        setSelectedLabels([]);
        setPastedCoverImage(null);
        setPastedCoverImageFile(null);
        handleClose();
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (!avoidMisfire) {
                event.target.blur();
                handleSaveClick();
            }
        }
    };

    const handleClose = () => {
        handleTaskModalClose();
        setLabelEditing(false);
        setLocalName("");
        setSelectedLabels([]);
        setPastedCoverImage(null);
        setPastedCoverImageFile(null);
    };

    const handleCloseLabelDialog = () => {
        setOpenLabelDialog(false);
        setLabelAdding(false);
    };

    const handleSetLabelAddOpen = () => {
        setLabelColor(getRandomHex());
        setLabelTitle("");
        setLabelAdding(true);
    };

    const handleSetLabelAddClose = () => {
        setLabelAdding(false);
    };

    const handleAddNewLabel = async (title, color, close = true, description) => {
        const response = await AddLabelToBoard(myWorkspaces[0].id, myBoard.project, myBoard.id, title, color, description);
        if (response.status === 201) {
            const newLabel = { ...response.data, usage_count: 0 };
            setSelectedLabels((prevLabels) => [...prevLabels, newLabel]);
            setMyBoard((prevBoard) => ({
                ...prevBoard,
                labels: [...prevBoard.labels, newLabel]
            }));
            showSnackbar(t("modal.label_added"), 'success');
        }
        if (close) {
            handleCloseLabelDialog();
        }
    };

    const handleEditClick = (label) => {
        setLabelEditing(true);
        setLabelToEdit(label);
    };

    const handleEditClose = () => {
        setLabelEditing(false);
        setLabelEditingTitle("");
        setLabelEditingColor("");
        setLabelEditingID("");
        setLabelToEdit("");
    };


    const handleEditLabel = async (title, color, label_id, description) => {
        const response = await UpdateLabel(workspace_id, myBoard.project, label_id, title, color, description);

        if (response.status === 200) {
            setSelectedLabels((prevLabels) => {
                const initializedLabels = prevLabels || [];
                const indexToUpdate = initializedLabels.findIndex(label => label.id === label_id);

                if (indexToUpdate !== -1) {

                    const updatedLabels = [
                        ...initializedLabels.slice(0, indexToUpdate),
                        response.data,
                        ...initializedLabels.slice(indexToUpdate + 1)
                    ];
                    return updatedLabels;
                }
                return initializedLabels;
            });

            setMyBoard((prevBoard) => {
                const updatedLabels = prevBoard.labels.map(label => label.id === label_id ? response.data : label);
                const updatedLists = prevBoard.lists.map(list => ({
                    ...list,
                    tasks: list.tasks.map(task => ({
                        ...task,
                        label: (task?.label || []).map(label => label.id === label_id ? response.data : label)
                    }))
                }));

                return {
                    ...prevBoard,
                    labels: updatedLabels,
                    lists: updatedLists
                };
            });


        }
        setLabelEditing(false);
    };

    const handleDeleteLabel = async (labelId) => {
        const updatedSelectedLabels = selectedLabels.filter((label) => label.id !== labelId);
        setSelectedLabels(updatedSelectedLabels);
    
        await DeleteLabel(myWorkspaces[0].id, myBoard.project, myBoard.id, labelId);
    
        setMyBoard((prevBoard) => ({
            ...prevBoard,
            labels: prevBoard.labels.filter((label) => label.id !== labelId)
        }));
    
        showSnackbar(t("modal.label_deleted"), 'success');
    
        DeleteLabelModalClose();
    };

    const handlePaste = (event) => {
        const clipboardItems = event.clipboardData.items;

        for (let item of clipboardItems) {
            if (item.type.startsWith('image')) {
                const file = item.getAsFile();
                setPastedCoverImage(URL.createObjectURL(file));
                setPastedCoverImageFile(file);
            }
        }

    };

    useEffect(() => {
        if (openNewTaskModal) {
            setPastedCoverImage(null);
            setPastedCoverImageFile(null);
            document.addEventListener('paste', handlePaste);
        }
    
        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [openNewTaskModal]);

    const handleLabelsOpen = () => {
        setOpenLabelsModal(true);
    }

    const handleLabelsClose = () => {
        if(labelAdding){
            setLabelAdding(false);
            return ;
        }

        if(labelEditing){
            setLabelEditing(false);
            setLabelToEdit(null)
            return;
        }

        setOpenLabelsModal(false);
    }

    const handleEditingClose = () => {
        setLabelEditing(false);
    }

    return (

        <Dialog
            open={openNewTaskModal}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            className="modal-background-color"
        >
            <DialogTitle className="white-txt-color">{t("modal.task_title")}</DialogTitle>
            <DialogContent
                className="create-task-dialog-content"
            >
                {pastedCoverImage && (
                    <Box className="create-task-pasted-cover">
                        <img
                            src={pastedCoverImage}
                            alt="Pasted Cover Preview"
                            className="create-task-pasted-cover-img"
                        />
                        <Typography variant="caption" color="textSecondary" className="white-txt-color create-task-cover-caption">
                            {t("modal.pasted_cover_image")}
                        </Typography>
                    </Box>
                )}
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label={t("global.name")}
                    type="text"
                    fullWidth
                    value={localName}
                    onChange={handleLocalNameChange}
                    onKeyPress={handleKeyPress}
                    InputLabelProps={{
                        className: "white-txt-color"
                    }}
                    InputProps={{
                        className: "white-txt-color"
                    }}
                    multiline
                    rows={2}
                />
                <Box>
                    {selectedLabels.length > 0 && (
                        <>
                            <Typography variant="caption" color="textSecondary" className="white-txt-color">
                                {t("modal.labels_selected")}
                            </Typography>
                            <Box className="create-task-labels">
                                {selectedLabels.map((label) => (
                                    <Typography
                                        key={label.id}
                                        variant="body2"
                                        className="create-task-label-margin"
                                        style={{ color: label.color }}
                                    >
                                        {label.title || t("modal.untitled")}
                                    </Typography>
                                ))}
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions className="create-task-dialog-actions">
                <Box className="create-task-actions-box">
                    <Button
                        variant="outlined"
                        startIcon={<ArrowDropDownIcon />}
                        onClick={handleLabelsOpen}
                        className="create-task-label-border"
                    >
                        {t("modal.labels_select")}
                    </Button>
                </Box>
                <Box className="buttons-flex">

                    <Button onClick={handleClose} className="decline-button create-task-cancel-button">
                        {t("global.cancel")}
                    </Button>
                    <Button onClick={handleSaveClick} disabled={avoidMisfire} className="accept-button">
                        {t("global.save")}
                    </Button>
                </Box>
            </DialogActions>

            {/* Label Dialog */}
            <LabelsComponent
                open={openLabelsModal}
                t={t}
                myBoardSortedLabels={myBoard?.labels}
                selectedTaskLabels={selectedLabels}
                labelAdding={labelAdding}
                labelEditing={labelEditing}
                labelEditingID={labelEditingID}
                toggleLabelSelection={handleLabelChange}
                handleEditClick={handleEditClick}
                DeleteLabelModalOpen={DeleteLabelModalOpen}
                handleSetLabelAddOpen={handleSetLabelAddOpen}
                handleSetLabelAddClose={handleSetLabelAddClose}
                handleSaveLabel={handleLabelsClose}
                handleSetCancelEdit={handleEditClose}
                handleAddNewLabel={handleAddNewLabel}
                handleEditLabel={handleEditLabel}
                handleClose={handleLabelsClose}
                labelToEdit={labelToEdit}
                handleEditingClose={handleEditingClose}
            />

            <DeleteLabelModal
                openDeleteLabelModal={openDeleteLabelModal}
                DeleteLabelModalClose={DeleteLabelModalClose}
                handleDeleteLabel={handleDeleteLabel}
                avoidMisfire={avoidMisfire}
                labelIdToDelete={labelIdToDelete}
            />
        </Dialog>
    );
}
